import { useEffect, useState } from "react";
import { FileText, Truck, IndianRupee } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { btnPrimary, cardCls, inputCls, labelCls } from "./shared";

const POLICY_FIELDS = [
  { key: "policy_return", label: "Return & Replacement Policy", testid: "policy-return" },
  { key: "policy_shipping", label: "Shipping Policy", testid: "policy-shipping" },
  { key: "policy_privacy", label: "Privacy Policy", testid: "policy-privacy" },
  { key: "policy_terms", label: "Terms & Conditions", testid: "policy-terms" },
];

export default function PoliciesTab({ settings, reload }) {
  const [form, setForm] = useState(null);
  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);
  if (!form) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const body = {};
    POLICY_FIELDS.forEach(({ key }) => { body[key] = form[key] || ""; });
    body.shipping_prepaid = form.shipping_prepaid === "" || form.shipping_prepaid == null ? 70 : Number(form.shipping_prepaid);
    body.shipping_cod = form.shipping_cod === "" || form.shipping_cod == null ? 120 : Number(form.shipping_cod);
    body.cod_enabled = !!form.cod_enabled;
    body.upi_enabled = !!form.upi_enabled;
    body.bank_enabled = !!form.bank_enabled;
    try {
      await api.put("/admin/site-settings", body);
      toast.success("Policies & charges saved");
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-policies-tab">
      <div className={cardCls}>
        <div className="flex items-center gap-3">
          <IndianRupee className="text-primary" size={20} />
          <p className="font-display text-lg">Shipping charges &amp; payment switches</p>
        </div>
        <p className="mt-2 text-xs font-light text-muted-foreground">These control what customers pay at checkout. Current rules are kept unless you change them here.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className={labelCls}>Prepaid shipping (UPI / bank) — ₹
            <input data-testid="shipping-prepaid-input" type="number" min="0" className={inputCls} value={form.shipping_prepaid ?? 70} onChange={set("shipping_prepaid")} /></label>
          <label className={labelCls}>COD advance shipping — ₹
            <input data-testid="shipping-cod-input" type="number" min="0" className={inputCls} value={form.shipping_cod ?? 120} onChange={set("shipping_cod")} /></label>
        </div>
        <div className="mt-4 flex flex-wrap gap-5">
          {[["cod_enabled", "COD available"], ["upi_enabled", "UPI available"], ["bank_enabled", "Bank transfer available"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-xs uppercase tracking-widest">
              <input type="checkbox" data-testid={`policies-${k}`} checked={!!form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))} className="accent-primary" /> {l}
            </label>
          ))}
        </div>
      </div>

      <div className={cardCls}>
        <div className="flex items-center gap-3">
          <FileText className="text-primary" size={20} />
          <p className="font-display text-lg">Policy pages</p>
        </div>
        <p className="mt-2 text-xs font-light text-muted-foreground">One paragraph per line. Leave a box empty to keep the current page text unchanged.</p>
        <div className="mt-4 space-y-5">
          {POLICY_FIELDS.map(({ key, label, testid }) => (
            <label key={key} className={labelCls}>{label}
              <textarea data-testid={`${testid}-input`} rows={5} className={inputCls} value={form[key] || ""} onChange={set(key)}
                placeholder="Leave empty to keep the current text" /></label>
          ))}
        </div>
      </div>

      <div className={cardCls}>
        <div className="flex items-center gap-3">
          <Truck className="text-primary" size={20} />
          <p className="font-display text-lg">Note</p>
        </div>
        <p className="mt-2 text-xs font-light leading-relaxed text-muted-foreground">
          Bank and UPI payment details are edited under Site &amp; Payments. Changes here go live on your policy pages and checkout immediately — no republishing needed.
        </p>
      </div>

      <button type="submit" data-testid="policies-save-btn" className={btnPrimary}>Save policies &amp; charges</button>
    </form>
  );
}
